/**
 * Banking Verification API client — Chunk 2.
 *
 * Mirrors backend routes added in Chunk 1:
 *   POST /api/ingestion/loan-files/:id/bank-statement/verify
 *   GET  /api/ingestion/loan-files/:id/banking-verifications
 *
 * The upload flow for a bank statement PDF is NOT through this client —
 * bank statement PDFs are uploaded via the existing ingestion createLoanFile /
 * registerDocumentUploaded flow (the file ends up as a document with
 * documentType='bank_statement'). This client triggers verification
 * (Novel Check dispatch) on an already-uploaded document, and then
 * polls for results.
 */

import { apiRequest } from './client';
import type {
  BankingVerificationDTO,
  BankingVerificationStatus,
} from '@loanaudit/shared';

export type { BankingVerificationDTO };

// -------------------------------------------------------------------------
// POST /api/ingestion/loan-files/:id/bank-statement/verify
// -------------------------------------------------------------------------

export interface TriggerVerificationRequest {
  documentId: string;
}

export interface TriggerVerificationResponse {
  ok: boolean;
  verificationId: string;
  status: BankingVerificationStatus;
  provider: string;
}

// -------------------------------------------------------------------------
// GET /api/ingestion/loan-files/:id/banking-verifications
// -------------------------------------------------------------------------

export interface BankingVerificationsListResponse {
  verifications: BankingVerificationDTO[];
}

// -------------------------------------------------------------------------
// API client object
// -------------------------------------------------------------------------

export const bankingVerificationApi = {
  /**
   * Trigger a Novel-Check-like banking verification on an already-uploaded
   * bank statement document. Creates a banking_verifications row and
   * enqueues the BullMQ job. Returns immediately — poll list() for results.
   */
  trigger: (loanFileId: string, documentId: string) =>
    apiRequest<TriggerVerificationResponse>(
      `/api/ingestion/loan-files/${loanFileId}/bank-statement/verify`,
      {
        method: 'POST',
        body: { documentId },
      },
    ),

  /**
   * List all banking verification rows for a loan file, newest first.
   * Poll this at a ~4s interval after triggering; stop when the latest
   * row reaches status "completed" or "failed".
   */
  list: (loanFileId: string) =>
    apiRequest<BankingVerificationsListResponse>(
      `/api/ingestion/loan-files/${loanFileId}/banking-verifications`,
    ),
};
